import {
  apiGet,
  type ApiRequestOptions,
} from "@softmax/observatory-client/client";
import type { Schemas } from "@softmax/observatory-client/types";
import { leagueRoute } from "@softmax/web-features/leagues";
import { buildHref, type GameAddress } from "@softmax/web-addresses";
import type { GamePageContext } from "@/features/game/state";
import { getAuthSession } from "@/features/identity/session";
import { navigationLeagues } from "./catalog";

type SelectedGame = { id: string; address: GameAddress };

export async function viewerProgress({
  context,
  signal,
}: {
  context?: GamePageContext | undefined;
  signal?: AbortSignal | undefined;
}): Promise<ReadonlyMap<string, string> | null> {
  const session = await getAuthSession({ signal });

  if (!session?.user) return null;

  let games: SelectedGame[];

  if (context) {
    games =
      context.gameId && context.address.catalog_status === "listed"
        ? [{ id: context.gameId, address: context.address }]
        : [];
  } else {
    const leagues = await navigationLeagues({ signal });
    games = leagues.map((league) => ({
      id: league.game.id,
      address: leagueRoute(league).game,
    }));
  }

  if (games.length === 0) return new Map();

  const query = new URLSearchParams();

  for (const game of games) query.append("game_id", game.id);

  const options: ApiRequestOptions = { elevatedPrivileges: false };

  if (signal !== undefined) options.signal = signal;

  const continuations = await apiGet<Schemas["ListedGameContinuation"][]>(
    `/v2/leagues/catalog/games/continuations?${query}`,
    options,
  );
  const byId = new Map(games.map((game) => [game.id, game]));
  const doors = new Map<string, string>();

  for (const continuation of continuations) {
    const game = byId.get(continuation.game_id)!;
    doors.set(
      game.address.slug,
      buildHref({
        name: "game.play",
        game: game.address,
        league: continuation.league_short_name,
      }),
    );
  }

  return doors;
}
